/* External */
import {
  memo, useMemo, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { isWithinIntervalDate } from '@dtvgo/date-utils';

/* Components */
import ScrollIntoViewIfFocused from '../ScrollIntoViewIfFocused';
import LiveCard from '../LiveCard';

/* Utils */
import { redirectToDetail, redirectToPlayer } from '../../utils/history';
import { noOp } from '../../utils/functions';
import useLockIcon from '../../hooks/useLockIcon';
import { Direction } from '../../utils/spatialNavigation';
import { useSubtitleCatalogCard } from './utils';
import { useTitle } from '../../utils/metaDataUtils';
import { Content, Show } from '../../storage/models';
import { getPlaySourcePage } from '../../utils/trackSourceConviva';

function CatalogLiveCard({
  content: schedule,
  width,
  blockScroll,
  position,
  handleArrowPress,
  setFocus,
  index,
  focusFirstCard,
  isLastCard,
  hideMetadata,
  dark,
  locationState,
}) {
  const {
    images,
  } = schedule || {};
  const isNonEntitledContent = useLockIcon(schedule);

  useEffect(() => {
    if (focusFirstCard && position === 0) {
      setFocus();
    }
  }, [focusFirstCard, position, setFocus]);

  const title = useTitle(schedule);
  const subtitle = useSubtitleCatalogCard(schedule);

  const style = useMemo(() => ({
    width,
  }), [width]);

  const catalogVodCardHandleArrowPress = useCallback((direction, props) => {
    if (direction === Direction.RIGHT && isLastCard) {
      return false;
    }

    handleArrowPress(direction, props);
    return true;
  }, [handleArrowPress, isLastCard]);

  const handleEnterPress = useCallback(() => {
    const startDate = schedule?.startTime
      ? new Date(schedule?.startTime)
      : undefined;
    const endDate = schedule?.endTime ? new Date(schedule?.endTime) : undefined;
    const isLive = isWithinIntervalDate(startDate, endDate);
    if (isLive) {
      redirectToPlayer(schedule, {
        page: getPlaySourcePage(),
        state: locationState({ content: schedule }),
      });
    } else {
      redirectToDetail(schedule, { state: locationState() });
    }
  }, [locationState, schedule]);

  return (
    <ScrollIntoViewIfFocused
      className="dtv-html5-c-catalog-all__card"
      position={blockScroll}
      style={style}
    >
      <LiveCard
        index={index}
        images={images}
        title={title}
        subtitle={subtitle}
        isNonEntitledContent={isNonEntitledContent}
        isLastCard={isLastCard}
        onArrowPress={catalogVodCardHandleArrowPress}
        onEnterPress={handleEnterPress}
        hideMetadata={hideMetadata}
        dark={dark}
        item={schedule}
      />
    </ScrollIntoViewIfFocused>
  );
}

CatalogLiveCard.propTypes = {
  index: PropTypes.number,
  focusFirstCard: PropTypes.bool,
  isLastCard: PropTypes.bool,
  hideMetadata: PropTypes.bool,
  dark: PropTypes.bool,
  content: PropTypes.oneOfType([
    PropTypes.instanceOf(Content),
    PropTypes.instanceOf(Show),
  ]),
  blockScroll: PropTypes.oneOf([
    'center',
    'end',
    'nearest',
    'start',
  ]).isRequired,
  width: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  handleArrowPress: PropTypes.func,
  setFocus: PropTypes.func.isRequired,
  locationState: PropTypes.instanceOf(Object),
};

CatalogLiveCard.defaultProps = {
  content: undefined,
  handleArrowPress: noOp,
  index: 0,
  focusFirstCard: false,
  isLastCard: false,
  hideMetadata: false,
  dark: false,
  locationState: {},
};

export default memo(withFocusable()(memo(CatalogLiveCard)));
